import React, { useState, useEffect } from "react";
import { standingsAPI, weeksAPI, handleApiError } from "../services/api";

const Standings = () => {
  const [standings, setStandings] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("overall");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchStandings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeek]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [standingsRes, weeksRes] = await Promise.all([
        standingsAPI.getAll(),
        weeksAPI.getAll(),
      ]);

      if (!Array.isArray(weeksRes.data) || !Array.isArray(standingsRes.data)) {
        throw new Error(
          "Unexpected API response — check that REACT_APP_API_URL points at the worker."
        );
      }

      setStandings(standingsRes.data);
      setWeeks(weeksRes.data);
    } catch (err) {
      setError(handleApiError(err, "Failed to fetch data"));
    } finally {
      setLoading(false);
    }
  };

  const fetchStandings = async () => {
    try {
      const response =
        selectedWeek === "overall"
          ? await standingsAPI.getAll()
          : await standingsAPI.getWeek(selectedWeek);
      setStandings(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(handleApiError(err, "Failed to fetch standings"));
    }
  };

  const getPercentageClass = (percentage) => {
    if (percentage >= 70) return "high";
    if (percentage >= 50) return "medium";
    return "low";
  };

  const formatWLRecord = (wins, total) => {
    const losses = total - wins;
    return `${wins}-${losses}`;
  };

  const getMostRecentWeekStats = (weeklyStats) => {
    if (!weeklyStats || weeklyStats.length === 0) {
      return { correct_picks: 0, total_picks: 0 };
    }
    // Get the last week (most recent) from the sorted array
    return weeklyStats[weeklyStats.length - 1];
  };

  if (loading) {
    return (
      <div className="loading">
        <h2>Loading standings...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <h2 style={{ color: "#f56565" }}>Error</h2>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={fetchData}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="app-header">
        <h1 className="page-title">Family Standings</h1>
        <p className="page-subtitle">
          Track your college football picking success
        </p>
      </div>

      <div className="week-selector">
        <label htmlFor="week-select">View standings for:</label>
        <select
          id="week-select"
          value={selectedWeek}
          onChange={(e) => setSelectedWeek(e.target.value)}
        >
          <option value="overall">Overall Season</option>
          {weeks.map((week) => (
            <option key={week.id} value={week.id}>
              Week {week.week_number} ({week.season})
              {/*{week.is_active && ' - Current'}*/}
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        <h2>
          {selectedWeek === "overall"
            ? "Season Leaderboard"
            : `Week ${
                weeks.find((w) => w.id === parseInt(selectedWeek))?.week_number
              } Standings`}
        </h2>

        {standings.length === 0 ? (
          <div className="empty-state">
            <h3>No standings available</h3>
            <p>No picks have been made yet for this period.</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Player</th>
                <th>Overall W-L</th>
                <th>Recent Week W-L</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((player, index) => {
                const recentWeekStats = getMostRecentWeekStats(player.weekly_stats);
                return (
                  <tr key={player.id}>
                    <td>
                      <span className="standings-rank">#{player?.rank || index + 1}</span>
                    </td>
                    <td>
                      <strong>{player.name}</strong>
                    </td>
                    <td>
                      <strong>{formatWLRecord(player.correct_picks, player.total_picks)}</strong>
                    </td>
                    <td>
                      <strong>{formatWLRecord(recentWeekStats.correct_picks, recentWeekStats.total_picks)}</strong>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {selectedWeek === "overall" && standings.length > 0 && (
        <div className="card">
          <h3>Weekly Performance</h3>
          <div className="grid grid-2">
            {standings.map((player) => (
              <div key={`weekly-${player.id}`} className="card">
                <h4>{player.name}'s Weekly Stats</h4>
                {player.weekly_stats && player.weekly_stats.length > 0 ? (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Week</th>
                        <th>W-L Record</th>
                        <th>Win %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {player.weekly_stats.map((week) => (
                        <tr key={`${player.id}-week-${week.week_number}`}>
                          <td>Week {week.week_number}</td>
                          <td>
                            <strong>{formatWLRecord(week.correct_picks, week.total_picks)}</strong>
                          </td>
                          <td>
                            <span
                              className={`standings-percentage ${getPercentageClass(
                                week.percentage
                              )}`}
                            >
                              {week.percentage.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>No weekly data available</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Standings;
